import { setBusinessUiPreference } from '~/services/business-ui/preference';
import { demoPlanFromQuery, enableDemoMode } from '~/services/demo/mode';

/**
 * The boot's reads, and the one thing they need installed first.
 *
 * `loadPersonFromAddress` tells the member with a toast when the account the address names will not
 * open, and this plugin awaits it. By filename this plugin runs FIRST — Nuxt registers plugins in
 * alphabetical order and `hooks` sorts before `toast` — so `vueApp.use(Toast)` had not happened yet
 * and the toast was emitted onto an event bus with no listener on it: dropped, never replayed. The
 * member got neither the fatal error page nor the explanation, just an empty dashboard, which is
 * worse than either. `dependsOn` is how `member-persisted-state.ts` says the same thing about Pinia.
 */
export default defineNuxtPlugin({
    name: 'boot-hooks',
    dependsOn: ['toast'],
    async setup() {
        const authStore = useAuthStore();
        const personStore = usePersonStore();
        const route = useRoute();

        if (route.path === '/auth/callback') {
            return;
        }

        // The auth app hands a demo over on the URL — the only channel it has, being a
        // different origin. Claiming it here rather than in route middleware is what makes
        // the account load: a demo holds no token, so without this the check below sees a
        // signed-out visitor and the dashboard renders with no person behind it. The value
        // carries which plan the demo runs on, so the states behind a paid capability are
        // reachable too.
        const demoPlan = demoPlanFromQuery(route.query.demo);
        if (demoPlan) {
            enableDemoMode(demoPlan);
        }

        // The legacy business app hands the UI preference over the same way, so a
        // staff member who switched to classic has a link back into the new portal.
        // `is_pb` still gates entry — this only clears the veto.
        if (route.query.newui === '1' || route.query.newui === '0') {
            setBusinessUiPreference(route.query.newui === '1' ? 'new' : 'classic');
        }

        if (authStore.hasSession) {
            await authStore.getMe();
            // The person on screen is the confirmed member or someone they manage (`?person=`). With
            // no member confirmed there is no one to load it for: a page that needs no session stays
            // signed out, so its request would be refused (staysSignedOut in stores/auth.ts), and the
            // route guard confirms the member of a page that needs one, then loads the person
            // (middleware/routing.global.ts).
            if (authStore.user) {
                await personStore.loadPersonFromAddress();
            }
        }
    },
});
